import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { ViewModelForm, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDocumentTitle, useWizardModals, WMICWizardChangeOrRenewalPage } from 'wmic-pe-portals-wizard-components-ui';
import { CONSTANTS, useCanJumpToWizardStepDispatch, WMICDriverUtil, WMICUserAccessUtil } from 'wmic-pe-portals-utils-js';
import { WMICDriverDetailView, useDriverActions } from 'wmic-pe-capability-gateway-common-pa-react';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-policyrenewal-common-react';
import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';

import messages from './WMICPAPolicyRenewalDriversDetailsPage.messages';
import metadata from './WMICPAPolicyRenewalDriversDetailsPage.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

function WMICPAPolicyRenewalDriversDetailsPage(props) {
    const { wizardData: policyRenewalVM, updateWizardData } = props;
    const { setWizardLoading } = useWizardModals();
    const { RenewalService } = useDependencies('RenewalService');
    const { authHeader, authUserData } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);
    const { initialValidation, isComponentValid, onValidate, registerInitialComponentValidation } = useValidation(
        'WMICPolicyRenewalPADriversDetailsPage'
    );
    const {
        initDriverData,
        onCreateDriverWithModal,
        onSaveDriver,
        onSaveDriverMiddleware,
        onDeleteDriver,
        renderAddDriverButton,
    } = useDriverActions({ jobVM: policyRenewalVM, updateWizardData, setShowErrors });
    const { wizardStepDispatch } = useCanJumpToWizardStepDispatch();

    useDocumentTitle(translator(messages.driversTitle), policyRenewalVM);

    useEffect(() => {
        registerInitialComponentValidation(() => _.get(policyRenewalVM, RISK_PATHS.DRIVERS_PATH).value.length > 0);

        initDriverData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditRenewal = useMemo(
        () => WMICUserAccessUtil.canEditRenewal(authUserData.permission_Ext),
        [authUserData.permission_Ext]
    );

    const onCancelDriver = () => {
        _.set(policyRenewalVM, 'isEditingPage.value', false);
        updateWizardData(policyRenewalVM);
        wizardStepDispatch({ type: CONSTANTS.WIZARD_ACTION_TYPES.RESET, vm: policyRenewalVM });
    };

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICDriverUtil.presentDriverList(policyRenewalVM)) {
            setShowErrors(true);

            return false;
        }

        if (canEditRenewal) {
            try {
                setWizardLoading(true, translator(commonMessages.savingTransactionDetails));

                policyRenewalVM.value = await RenewalService.saveRenewal([policyRenewalVM.value], authHeader);

                updateWizardData(policyRenewalVM);

                return policyRenewalVM;
            } finally {
                setWizardLoading(false);
            }
        }

        return false;
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentValid, canEditRenewal, setWizardLoading, translator, policyRenewalVM, RenewalService, authHeader]);

    const overrideProps = {
        atLeastOneMsgContainer: {
            visible: _.isEmpty(_.get(policyRenewalVM, `${RISK_PATHS.DRIVERS_PATH}.value`, [])),
            message: messages.atLeastOne,
        },
        requiredText: {
            visible: _.get(policyRenewalVM, RISK_PATHS.DRIVERS_PATH).length > 0,
        },
        driverListView: {
            value: _.get(policyRenewalVM, `${RISK_PATHS.DRIVERS_PATH}.children`, []),
            clickable: true,
            startOpen: true,
            readOnly: !canEditRenewal,
            VMData: [
                {
                    headerText: translator(messages.driverName),
                    path: 'displayName',
                },
                {
                    headerText: translator(messages.licenseNumber),
                    path: 'licenseNumber',
                },
                {
                    headerText: translator(messages.licenseProvince),
                    path: 'licenseState',
                    getData: (row, path) => _.get(row.value, path) || translator(messages.licenseProvinceOther),
                },
            ],
            toCreate: onCreateDriverWithModal,
            onSave: onSaveDriver,
            onSaveMiddleware: onSaveDriverMiddleware,
            onDelete: onDeleteDriver,
            toUndoCreate: () => {
                const drivers = _.get(policyRenewalVM.value, RISK_PATHS.DRIVERS_PATH);
                drivers.splice(drivers.length - 1, 1);
                _.set(policyRenewalVM.value, RISK_PATHS.DRIVERS_PATH, drivers);
                updateWizardData(policyRenewalVM);
            },
            detailViewComponent: WMICDriverDetailView,
            onValidate,
            renderAddButton: renderAddDriverButton,
            detailViewComponentProps: {
                jobVM: policyRenewalVM,
            },
            onCancel: onCancelDriver,
            showCancelModal: true,
        },
    };

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            cancelLabel={translator(commonMessages.saveAndExit)}
            isSkipping={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyRenewalVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                showErrors={showErrors}
                onValidationChange={onValidate}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPAPolicyRenewalDriversDetailsPage.propTypes = wizardProps;

export default withViewModelService(WMICPAPolicyRenewalDriversDetailsPage);
